'use client';

import * as React from 'react';
import Image from 'next/image';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { useTranslations } from 'next-intl';
import type { SustainabilityGovernanceVisionCardData } from '@/types/sustainability';

interface SustainabilityGovernanceVisionSectionProps {
  locale?: string;
  cmsHeading?: string | null;
  cmsSubheadingHtml?: string | null;
  cmsLeftCard?: SustainabilityGovernanceVisionCardData | null;
  cmsRightCard?: SustainabilityGovernanceVisionCardData | null;
}

type VisionCardItem = {
  id: string;
  text?: string;
  heading?: string;
  descriptionHtml?: string;
};

export default function SustainabilityGovernanceVisionSection({
  cmsHeading,
  cmsSubheadingHtml,
  cmsLeftCard,
  cmsRightCard,
}: SustainabilityGovernanceVisionSectionProps = {}) {
  const t = useTranslations('sustainability.pages.governance');
  const sectionRef = React.useRef<HTMLElement>(null);
  const pinRef = React.useRef<HTMLDivElement>(null);
  const leftCardRef = React.useRef<HTMLDivElement>(null);
  const rightCardRef = React.useRef<HTMLDivElement>(null);

  const leftItems: VisionCardItem[] = cmsLeftCard
    ? cmsLeftCard.bullets.map((b, i) => ({
        id: `l-cms-${i}`,
        heading: b.heading,
        descriptionHtml: b.descriptionHtml,
      }))
    : [
        { id: 'l1', text: t('governanceVisionLeftPoint1') },
        { id: 'l2', text: t('governanceVisionLeftPoint2') },
        { id: 'l3', text: t('governanceVisionLeftPoint3') },
      ];

  const rightItems: VisionCardItem[] = cmsRightCard
    ? cmsRightCard.bullets.map((b, i) => ({
        id: `r-cms-${i}`,
        heading: b.heading,
        descriptionHtml: b.descriptionHtml,
      }))
    : [
        { id: 'r1', text: t('governanceVisionRightPoint1') },
        { id: 'r2', text: t('governanceVisionRightPoint2') },
        { id: 'r3', text: t('governanceVisionRightPoint3') },
      ];

  const heading = cmsHeading || t('governanceVisionHeading');
  const leftTitle = cmsLeftCard?.title || t('governanceVisionLeftTitle');
  const rightTitle = cmsRightCard?.title || t('governanceVisionRightTitle');

  React.useLayoutEffect(() => {
    if (typeof window === 'undefined') return;
    if (!window.matchMedia('(min-width: 1024px)').matches) return;
    gsap.registerPlugin(ScrollTrigger);

    const trigger = sectionRef.current;
    const pinEl = pinRef.current;
    const leftEl = leftCardRef.current;
    const rightEl = rightCardRef.current;
    if (!trigger || !pinEl || !leftEl || !rightEl) return;

    const ctx = gsap.context(() => {
      gsap.set(leftEl, { y: 120, autoAlpha: 0 });
      gsap.set(rightEl, { y: -120, autoAlpha: 0 });

      gsap
        .timeline({
          scrollTrigger: {
            trigger,
            start: 'top top',
            end: '+=90%',
            pin: pinEl,
            scrub: 0.6,
            anticipatePin: 1,
            invalidateOnRefresh: true,
          },
        })
        .to(leftEl, { y: 0, autoAlpha: 1, duration: 0.45, ease: 'power2.out' }, 0)
        .to(rightEl, { y: 0, autoAlpha: 1, duration: 0.45, ease: 'power2.out' }, 0);
    }, trigger);

    return () => {
      ctx.revert();
    };
  }, []);

  return (
    <section ref={sectionRef} className="relative bg-[#efefef] text-[#111] max-sm:py-12">
      <div ref={pinRef} className="flex items-center lg:min-h-[100svh]">
        <div className="mx-auto w-full max-w-[1200px] max-sm:px-4">
          <div className="mx-auto max-w-4xl text-center">
            <h2 className="text-[1.85rem] font-semibold leading-[1.1] tracking-tight text-black sm:text-[2.4rem] md:text-[2.85rem] lg:text-[3.45rem]">
              {heading}
            </h2>
            {cmsSubheadingHtml ? (
              <div
                className="mx-auto mt-3 max-w-[50rem] text-[12px] leading-relaxed text-black/80 sm:mt-4 sm:text-[13px] [&_p]:m-0 [&_strong]:font-semibold"
                dangerouslySetInnerHTML={{ __html: cmsSubheadingHtml }}
              />
            ) : (
              <p className="mx-auto mt-3 max-w-[50rem] text-[12px] leading-relaxed text-black/80 sm:mt-4 sm:text-[13px]">
                {t('governanceVisionIntro')}
              </p>
            )}
          </div>

          <div className="mt-8 grid gap-4 sm:mt-10 lg:mt-12 lg:grid-cols-2 lg:gap-5">
            <article
              ref={leftCardRef}
              className="rounded-xl border border-black/20 bg-black px-4 py-5 text-white shadow-[0_6px_20px_rgba(0,0,0,0.15)] sm:px-6 sm:py-7"
            >
              <h3 className="text-[1.5rem] font-semibold leading-[1.1] text-white sm:text-[1.75rem] md:text-[2rem]">
                {leftTitle}
              </h3>
              <ul className="mt-4 space-y-3 sm:mt-6 sm:space-y-4">
                {leftItems.map((item) => (
                  <li key={item.id} className="flex items-start gap-3">
                    <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-white">
                      <Image
                        src="/images/double-check.svg"
                        alt=""
                        width={12}
                        height={12}
                        aria-hidden
                      />
                    </span>
                    {item.descriptionHtml ? (
                      <p className="text-[13px] leading-relaxed text-white/85 sm:text-[14px] [&_p]:m-0 [&_p]:inline [&_strong]:font-semibold [&_strong]:text-white">
                        <strong className="font-semibold text-white">{item.heading}</strong>{' '}
                        <span dangerouslySetInnerHTML={{ __html: item.descriptionHtml }} />
                      </p>
                    ) : (
                      <p className="text-[13px] leading-relaxed text-white/85 sm:text-[14px]">
                        {item.text}
                      </p>
                    )}
                  </li>
                ))}
              </ul>
            </article>

            <article
              ref={rightCardRef}
              className="rounded-xl border border-dashed border-black/20 bg-[#efefef] px-4 py-5 text-black sm:px-6 sm:py-7"
            >
              <h3 className="text-[1.5rem] font-semibold leading-[1.1] text-black sm:text-[1.75rem] md:text-[2rem]">
                {rightTitle}
              </h3>
              <ul className="mt-4 space-y-3 sm:mt-6 sm:space-y-4">
                {rightItems.map((item) => (
                  <li key={item.id} className="flex items-start gap-3">
                    <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-black">
                      <Image
                        src="/images/double-check.svg"
                        alt=""
                        width={12}
                        height={12}
                        className="invert"
                        aria-hidden
                      />
                    </span>
                    {item.descriptionHtml ? (
                      <p className="text-[13px] leading-relaxed text-black/85 sm:text-[14px] [&_p]:m-0 [&_p]:inline [&_strong]:font-semibold [&_strong]:text-black">
                        <strong className="font-semibold text-black">{item.heading}</strong>{' '}
                        <span dangerouslySetInnerHTML={{ __html: item.descriptionHtml }} />
                      </p>
                    ) : (
                      <p className="text-[13px] leading-relaxed text-black/85 sm:text-[14px]">
                        {item.text}
                      </p>
                    )}
                  </li>
                ))}
              </ul>
            </article>
          </div>
        </div>
      </div>
    </section>
  );
}
